import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, Star, Heart, CheckCircle, Smile, Package } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const GiftingPlatforms = () => {
  const benefits = [
    {
      icon: <Heart className="h-6 w-6 text-primary" />,
      title: "Show Appreciation",
      description: "Thoughtful gifts create emotional connections and show genuine care for employees"
    },
    {
      icon: <Smile className="h-6 w-6 text-primary" />,
      title: "Celebrate Milestones",
      description: "Mark important achievements and life events with meaningful gifts"
    },
    {
      icon: <Package className="h-6 w-6 text-primary" />,
      title: "Seamless Delivery",
      description: "Automated gifting workflows ensure timely delivery for any occasion"
    }
  ];

  const vendors = [
    {
      name: "Assembly",
      rating: 4.8,
      description: "Integrated gifting platform with curated selections, milestone tracking, and personalized delivery.",
      features: ["Curated Gift Catalog", "Milestone Automation", "Personal Messaging", "Global Shipping"],
      badge: "Editor's Choice"
    },
    {
      name: "Caroo",
      rating: 4.7,
      description: "Corporate gifting platform specializing in care packages and wellness-focused gift options.",
      features: ["Care Packages", "Wellness Gifts", "Custom Branding", "Bulk Ordering"],
      badge: "Best Curation"
    },
    {
      name: "Giftagram",
      rating: 4.5,
      description: "Digital-first gifting platform with instant delivery and personalized gift experiences.",
      features: ["Digital Delivery", "Gift Cards", "Experience Gifts", "Mobile App"],
      badge: "Most Convenient"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Employee Gifting Platforms | Updated Annually</title>
        <meta name="description" content="Find the best employee gifting platforms to celebrate achievements and show appreciation. Updated annually with vendor comparisons and feature reviews." />
        <meta name="keywords" content="best employee gifting platforms, corporate gifting software, employee gift platforms, gifting platform comparison" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Best Employee Gifting Platforms",
            "description": "Top-rated employee gifting platforms",
            "itemListElement": vendors.map((vendor, index) => ({
              "@type": "SoftwareApplication",
              "position": index + 1,
              "name": vendor.name,
              "description": vendor.description,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": vendor.rating,
                "bestRating": 5
              }
            }))
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <div className="mb-6">
              <nav className="text-sm text-muted-foreground mb-4">
                <a href="/best" className="hover:text-primary">Best Software</a> / Employee Gifting Platforms
              </nav>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Best Employee Gifting Platforms
              </h1>
              <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed mb-8">
                Employee gifting platforms help organizations celebrate achievements and show appreciation through thoughtful gifts. 
                These solutions offer curated catalogs, automated delivery, and personalized experiences that create lasting memories.
              </p>
              
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary font-medium">
                <Calendar className="h-4 w-4" />
                Last Updated: January 2025
              </div>
            </div>
          </section>

          {/* Why Gifting Platforms Matter */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Why Employee Gifting Platforms Matter</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      {benefit.icon}
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>

          {/* Annual Rankings */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Annual Rankings</h2>
            <div className="bg-muted/30 rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      2025 Rankings
                    </CardTitle>
                    <CardDescription>
                      Our comprehensive 2025 analysis of the best employee gifting platforms.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      2026 Rankings
                    </CardTitle>
                    <CardDescription>
                      Stay tuned for our 2026 rankings with the latest market developments.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Vendor Spotlights */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Top Employee Gifting Platforms</h2>
            <div className="space-y-6">
              {vendors.map((vendor, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center font-bold text-2xl">
                          {vendor.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-2xl">{vendor.name}</CardTitle>
                            {vendor.badge && (
                              <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {vendor.badge}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-semibold">{vendor.rating}</span>
                            </div>
                            <span className="text-muted-foreground">out of 5</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{vendor.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {vendor.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-1 text-sm bg-muted px-3 py-1 rounded-full">
                          <CheckCircle className="h-3 w-3 text-primary" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="px-6 max-w-7xl mx-auto">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Start Gifting?</h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Compare these top gifting platforms and find the perfect solution to celebrate your team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg">
                    Book a Demo
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <a href="/best">View All Categories</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default GiftingPlatforms;